/**
 * The one way to call `useSessionStore().createSession` and get an honest
 * answer back.
 *
 * The store's `createSession` returns `undefined` on several failure paths
 * (policy denial handled as a throw; DB/track/lineage failures returned as
 * `undefined` with the reason left on `lastCreateError`) and, on two
 * "warn-and-proceed" gates (an incomplete decision loop, unreviewed track
 * relationships), lets creation succeed while only `console.warn`-ing the
 * reason. Before this helper, every call site duplicated its own reading (or
 * non-reading) of `lastCreateError`, so most surfaces showed a generic
 * "Failed to create session" no matter what actually happened, and the
 * warn-and-proceed notes never reached the user at all.
 *
 * Every session-creation surface in the app calls this instead of the store
 * action directly (see `createSessionFlow.callSites.test.ts` for the
 * enforced list). Each surface keeps its own form/wizard — this unifies the
 * *result handling*, not the UI.
 */
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import type { PrioritizationSession } from '@/types/prioritization/session';
import type { ToastType } from '@/components/ui/Toast';

/** Matches `useSessionStore().createSession`'s parameter type exactly. */
export type CreateSessionInput = Parameters<
  ReturnType<typeof useSessionStore.getState>['createSession']
>[0];

/** Named reasons a create attempt was blocked, surfaced by the store's own signals. */
export type CreateSessionBlockedBy =
  | 'stage_mechanism_invalid'
  | 'pool_violations'
  | 'authorization_denied';

export interface CreateSessionFlowResult {
  /** True only when the session was actually created. */
  ok: boolean;
  /** The new session's id, present only when `ok`. */
  sessionId?: string;
  /** The new session, present only when `ok`. */
  session?: PrioritizationSession;
  /** The real reason creation failed, present only when `!ok`. */
  error?: string;
  /** Which hard gate refused the create, when known. */
  blockedBy?: CreateSessionBlockedBy;
  /**
   * A non-blocking note from a warn-and-proceed gate the session passed
   * through anyway. Present only when `ok` and a gate had something to say.
   */
  warning?: string;
}

export type ShowToastFn = (type: ToastType, title: string, message?: string, duration?: number) => void;

export interface CreateSessionFlowOptions {
  /**
   * Called with the new session's canonical detail route
   * (`/prioritization/sessions/:id`) immediately after a successful create.
   * Omit when the surface has its own destination (a host view, a specific
   * tab, an `onCreated` callback) — most surfaces do, and route themselves
   * using the returned `sessionId` instead of passing this.
   */
  navigate?: (path: string) => void;
  /**
   * Shown for the one non-blocking, user-facing case this helper produces
   * itself: "Created — note: …" when a warn-and-proceed gate let creation
   * through. Errors are returned as `error` for the caller's own inline
   * display (the existing pattern on every surface), not toasted here, so a
   * failure is never reported twice.
   */
  toast?: ShowToastFn;
}

/**
 * Reads the store's blocked-event signals for the duration of one
 * `createSession` call. The store dispatches `session-blocked` (with a
 * `reason` of `stage_mechanism_invalid` or `pool_violations`) and
 * `authorization-denied` on its hard-block paths and then throws a plain
 * Error — this captures the structured reason before the throw's message is
 * all that is left, without changing which gates block or how.
 */
function captureBlockedBy(): { read: () => CreateSessionBlockedBy | undefined; stop: () => void } {
  let blockedBy: CreateSessionBlockedBy | undefined;
  if (typeof window === 'undefined') {
    return { read: () => undefined, stop: () => {} };
  }
  const onBlocked = (event: Event) => {
    const reason = (event as CustomEvent<{ reason?: string }>).detail?.reason;
    if (reason === 'stage_mechanism_invalid' || reason === 'pool_violations') {
      blockedBy = reason;
    }
  };
  const onDenied = () => {
    blockedBy = 'authorization_denied';
  };
  window.addEventListener('session-blocked', onBlocked);
  window.addEventListener('authorization-denied', onDenied);
  return {
    read: () => blockedBy,
    stop: () => {
      window.removeEventListener('session-blocked', onBlocked);
      window.removeEventListener('authorization-denied', onDenied);
    },
  };
}

/** Pure message mapping, exported for unit testing without the store. */
export function describeCreateSessionFailure(
  error: string | null | undefined,
  blockedBy?: CreateSessionBlockedBy,
): string {
  if (error && error.trim()) return error;
  if (blockedBy === 'authorization_denied') {
    return 'You do not have permission to create sessions. Ask your workspace admin to grant you the Facilitate Sessions permission.';
  }
  if (blockedBy === 'pool_violations') {
    return "Cannot start ranking — this pool isn't comparable yet. Resolve the flagged issues before ranking.";
  }
  if (blockedBy === 'stage_mechanism_invalid') {
    return 'The selected method is not valid at this stage.';
  }
  return 'Session could not be created. Please try again.';
}

/** Pure message mapping, exported for unit testing without the store. */
export function describeCreateSessionWarning(warning: string): string {
  return `Created — note: ${warning}`;
}

/**
 * Calls `useSessionStore().createSession(input)` and returns one honest,
 * consistently-shaped result. Never throws — a thrown gate error and a
 * quiet `undefined` return are both normalized into `{ ok: false, error }`.
 */
export async function createSessionWithFeedback(
  input: CreateSessionInput,
  options: CreateSessionFlowOptions = {},
): Promise<CreateSessionFlowResult> {
  const blockedByCapture = captureBlockedBy();
  let session: PrioritizationSession | undefined;
  let thrownMessage: string | undefined;
  try {
    session = await useSessionStore.getState().createSession(input);
  } catch (err) {
    thrownMessage = err instanceof Error ? err.message : String(err);
  } finally {
    blockedByCapture.stop();
  }

  if (!session) {
    const blockedBy = blockedByCapture.read();
    const storeError = useSessionStore.getState().lastCreateError;
    const error = describeCreateSessionFailure(thrownMessage ?? storeError, blockedBy);
    return { ok: false, error, blockedBy };
  }

  const warning = useSessionStore.getState().lastCreateWarning ?? undefined;
  if (warning) {
    options.toast?.('info', describeCreateSessionWarning(warning));
  }
  options.navigate?.(`/prioritization/sessions/${session.id}`);

  return { ok: true, sessionId: session.id, session, warning };
}
